import { useEffect, useState } from "react";
import { ShieldCheck, XCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

const STATUS_STYLES = {
  pending: "border-gold/40 bg-gold/10 text-gold",
  verified: "border-signal/40 bg-signal/10 text-signal",
  rejected: "border-loss/40 bg-loss/10 text-loss",
};

export default function ProofsTab() {
  const [proofs, setProofs] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = () =>
    api.get("/admin/payout-proofs").then(({ data }) => setProofs(data)).catch(() => setProofs([]));

  useEffect(() => { load(); }, []);

  const act = async (p, action) => {
    setBusyId(p.id);
    try {
      await api.post(`/admin/payout-proofs/${p.id}/${action}`);
      toast.success(action === "approve" ? "PROOF VERIFIED" : "PROOF REJECTED");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete this certificate from ${p.user_name || p.user_email} (${p.firm_name}${p.amount ? ` · ${p.amount}` : ""})? This cannot be undone.`)) return;
    setBusyId(p.id);
    try {
      await api.delete(`/admin/payout-proofs/${p.id}`);
      toast.success("CERTIFICATE DELETED");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (proofs === null)
    return <p className="font-mono text-xs uppercase tracking-[0.25em] text-muted">LOADING CERTIFICATES…</p>;

  const token = localStorage.getItem("te_token");

  return (
    <div data-testid="admin-proofs-tab">
      <p className="mb-6 font-mono text-[10px] uppercase tracking-[0.2em] text-muted">
        {proofs.length} payout certificates · {proofs.filter((p) => p.status === "pending").length} awaiting review
      </p>
      <div className="space-y-3" data-testid="admin-proofs-list">
        {proofs.length === 0 && (
          <p className="card-violet p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-muted" data-testid="admin-proofs-empty">
            No certificates submitted yet.
          </p>
        )}
        {proofs.map((p) => (
          <div key={p.id} className="card-violet flex flex-wrap items-center gap-5 p-5" data-testid={`admin-proof-${p.id}`}>
            <a href={`${api.defaults.baseURL}/payout-proofs/file/${p.id}?auth=${token}`} target="_blank" rel="noopener noreferrer"
              data-testid={`proof-image-${p.id}`} className="shrink-0">
              <img
                src={`${api.defaults.baseURL}/payout-proofs/file/${p.id}?auth=${token}`}
                alt="payout proof"
                className="h-16 w-24 rounded-xl border border-line object-cover transition-opacity hover:opacity-80"
              />
            </a>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-sm font-700 text-white">{p.user_name || p.user_email}</p>
              <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-muted">
                {p.firm_name}{p.amount ? ` · ${p.amount}` : ""} · {new Date(p.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </p>
              <p className="mt-0.5 truncate font-mono text-[10px] text-muted">{p.original_filename}</p>
            </div>
            <span className={`rounded-full border px-3 py-1 font-mono text-[9px] font-700 uppercase tracking-[0.15em] ${STATUS_STYLES[p.status]}`}
              data-testid={`proof-status-${p.id}`}>
              {p.status}
            </span>
            <span className="flex gap-2">
              {p.status === "pending" && (
                <>
                  <button onClick={() => act(p, "approve")} disabled={busyId === p.id} data-testid={`approve-proof-${p.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-signal/50 px-4 py-2 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-signal transition-colors hover:bg-signal/10 disabled:opacity-50">
                    <ShieldCheck size={12} /> Verify
                  </button>
                  <button onClick={() => act(p, "reject")} disabled={busyId === p.id} data-testid={`reject-proof-${p.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-loss/50 px-4 py-2 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-loss transition-colors hover:bg-loss/10 disabled:opacity-50">
                    <XCircle size={12} /> Reject
                  </button>
                </>
              )}
              <button onClick={() => remove(p)} disabled={busyId === p.id} data-testid={`delete-proof-${p.id}`}
                className="inline-flex items-center gap-1.5 rounded-full border border-line px-4 py-2 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-muted transition-colors hover:border-loss/60 hover:text-loss disabled:opacity-50">
                <Trash2 size={12} /> Delete
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
